import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Loader2, Crosshair, Eye, EyeOff, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

import { tr } from "@/lib/i18n";
const CHUNK = 1024 * 1024;
export const fileUrl = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/files/${id}`;

const imgSize = (file) => new Promise((res, rej) => {
  const im = new window.Image();
  im.onload = () => { res([im.naturalWidth, im.naturalHeight]); URL.revokeObjectURL(im.src); };
  im.onerror = () => rej(new Error(tr("Immagine non leggibile")));
  im.src = URL.createObjectURL(file);
});

async function uploadImage(pid, file, onProgress) {
  const { data } = await api.post(`/projects/${pid}/uploads`, { filename: file.name, content_type: file.type, size: file.size });
  for (let o = 0; o < file.size; o += CHUNK) {
    await api.put(`/uploads/${data.upload_id}/chunk`, file.slice(o, o + CHUNK), { headers: { "Content-Type": "application/octet-stream" } });
    onProgress(Math.min(100, Math.round(((o + CHUNK) / file.size) * 100)));
  }
  return (await api.post(`/uploads/${data.upload_id}/complete`)).data;
}

// foto da drone / pianta come sfondo: caricamento, georeferenziazione a 2 punti, trasparenza
export const BackgroundPanel = ({ p, update, ed }) => {
  const bg = p.background || {};
  const inp = useRef(null);
  const [busy, setBusy] = useState(null);
  const [pick, setPick] = useState(null);
  const set = (patch) => update((prev) => ({ background: { ...(prev.background || {}), ...patch } }));

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!["image/jpeg", "image/png"].includes(file.type)) return toast.error(tr("Formato non supportato: carica un JPG o un PNG"));
    if (file.size > 30 * 1024 * 1024) return toast.error(tr("File troppo grande (max 30 MB)"));
    try {
      const [w, h] = await imgSize(file);
      setBusy(0);
      const r = await uploadImage(p.id, file, setBusy);
      if (bg.file_id) api.delete(`/files/${bg.file_id}`).catch(() => {});
      update({ background: { file_id: r.file_id, name: r.name, w, h, opacity: 0.7, visible: true, px: null, ll: null } });
      toast.success(tr("Immagine caricata: ora georeferenziala con 2 punti"));
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || tr("Caricamento non riuscito"));
    } finally { setBusy(null); }
  };

  const clickPhoto = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const q = [((e.clientX - r.left) / r.width) * bg.w, ((e.clientY - r.top) / r.height) * bg.h];
    const pts = [...pick, q];
    if (pts.length < 2) return setPick(pts);
    if (Math.hypot(pts[1][0] - pts[0][0], pts[1][1] - pts[0][1]) < Math.max(bg.w, bg.h) * 0.05) return toast.error(tr("Scegli due punti più lontani tra loro"));
    setPick(null);
    ed.setGeoref({ px: pts, ll: [] });
    ed.setTool("georef");
  };

  return (
    <div className="space-y-2" data-testid="editor-background">
      <input ref={inp} type="file" accept="image/jpeg,image/png" className="hidden" onChange={onFile} data-testid="editor-bg-file" />
      <button type="button" disabled={busy !== null} onClick={() => inp.current?.click()} data-testid="editor-bg-upload"
        className="w-full h-9 rounded-md border border-slate-200 text-sm text-slate-600 hover:bg-slate-50 flex items-center justify-center gap-1.5 disabled:opacity-60">
        {busy !== null ? <><Loader2 className="w-4 h-4 animate-spin" />{tr("Caricamento")} {busy}%</> : <><Upload className="w-4 h-4" />{bg.file_id ? tr("Sostituisci immagine") : tr("Carica foto o pianta (JPG, PNG)")}</>}
      </button>
      {bg.file_id && (
        <>
          <div className="flex items-center gap-1.5 text-xs">
            <span className="flex-1 truncate text-slate-600" data-testid="editor-bg-name">{bg.name}</span>
            <span className={ed.bgError ? "text-red-700" : bg.px ? "text-emerald-700" : "text-amber-700"} data-testid="editor-bg-status">{ed.bgError ? tr("file non disponibile") : bg.px ? "georeferenziata" : tr("da georeferenziare")}</span>
            <button type="button" title={bg.visible === false ? tr("Mostra") : tr("Nascondi")} onClick={() => set({ visible: bg.visible === false })} data-testid="editor-bg-toggle"
              className="w-7 h-7 grid place-items-center rounded border border-slate-200 text-slate-500">{bg.visible === false ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}</button>
            <button type="button" title={tr("Rimuovi sfondo")} data-testid="editor-bg-remove" onClick={() => { api.delete(`/files/${bg.file_id}`).catch(() => {}); update({ background: {} }); }}
              className="w-7 h-7 grid place-items-center rounded border border-slate-200 text-slate-400 hover:text-amber-700"><Trash2 className="w-3.5 h-3.5" /></button>
          </div>
          <button type="button" onClick={() => setPick([])} data-testid="editor-bg-georef"
            className={`w-full h-9 rounded-md text-sm flex items-center justify-center gap-1.5 ${ed.tool === "georef" ? "bg-amber-100 text-amber-800 border border-amber-300" : "bg-[#0F52BA] text-white"}`}>
            <Crosshair className="w-4 h-4" />{ed.tool === "georef" ? tr("Clicca il punto {0} sulla mappa", [(ed.georef?.ll.length || 0) + 1]) : tr("Georeferenzia (2 punti)")}
          </button>
          <label className="block text-xs text-slate-500">{tr("Trasparenza")} {Math.round((1 - (bg.opacity ?? 0.7)) * 100)}%
            <input type="range" min={0.1} max={1} step={0.05} value={bg.opacity ?? 0.7} data-testid="editor-bg-opacity"
              onChange={(e) => set({ opacity: Number(e.target.value) })} className="w-full accent-[#0F52BA]" />
          </label>
        </>
      )}
      <Dialog open={pick !== null} onOpenChange={(o) => !o && setPick(null)}>
        <DialogContent className="max-w-4xl" data-testid="editor-bg-dialog">
          <DialogHeader><DialogTitle>{tr("Clicca")} {pick?.length ? tr("il 2° punto") : tr("il 1° punto")} {tr("sulla foto")}</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500 -mt-2">{tr("Scegli due punti ben riconoscibili e lontani tra loro (es. due angoli del tetto). Poi cliccherai gli stessi punti sulla mappa satellitare.")}</p>
          {bg.file_id && (
            <div className="flex justify-center"><div className="relative inline-block cursor-crosshair select-none" onClick={clickPhoto} data-testid="editor-bg-photo">
              <img src={fileUrl(bg.file_id)} alt={bg.name} className="block max-h-[65vh] max-w-full w-auto h-auto" draggable={false} />
              {(pick || []).map((q, i) => (
                <span key={i} className="absolute w-6 h-6 -ml-3 -mt-3 rounded-full bg-amber-500 text-white text-xs grid place-items-center ring-2 ring-white"
                  style={{ left: `${(q[0] / bg.w) * 100}%`, top: `${(q[1] / bg.h) * 100}%` }}>{i + 1}</span>
              ))}
            </div></div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
